import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  FormBuilder
} from '@angular/forms';

import {
  CampusWard,
  ConfirmDialog,
  IComponentHasChanges,
  MessageDialog,
  CampusWardsDataService,
  CampusWardDetailsForUpdate
} from '../../shared';
import { MatDialog } from '@angular/material/dialog';
import * as Config from '../../shared/tokens/constants.tokens';
import { finalize } from 'rxjs/operators';

@Component({
  templateUrl: './campus-wards.component.html',
  styleUrls: ['./campus-wards.component.scss']
})
export class CampusWardsComponent implements OnInit, IComponentHasChanges {
  loading = false;
  hasLoadingError = false;
  campusWards: CampusWard[];

  selectedCampusWard: CampusWard;
  loadingWardDetails = false;

  campusWardForm: FormGroup;
  campusWard: CampusWardDetailsForUpdate;
  actionTitle: string;

  deleting = false;
  deleted = false;

  saving = false;
  saved = false;

  showPassword: boolean;

  constructor(
    private wardService: CampusWardsDataService,
    private formBuilder: FormBuilder,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.showPassword = false;
    this.campusWardForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(80)]],
      unitNumber: [
        Config.Constants.unassignedUnitNumber,
        [
          Validators.required,
          Validators.pattern(Config.Constants.unitNumberRegex)
        ]
      ],
      isForMarriedStudents: [false, [Validators.required]],
      bishopOffice: ['', [Validators.maxLength(50)]],
      phone: ['', [Validators.pattern(Config.Constants.phoneRegex)]],
      // As of 2018-3-6, the email validator assumed it was a required field.  This let's us around that.
      email: [
        '',
        [
          Validators.maxLength(70),
          Validators.pattern(Config.Constants.emailRegex)
        ]
      ],
      password: ['', Validators.required]
    });

    this.loadData();
  }

  /**
   * Indicates if we have unsaved changes to the ward's information.
   */
  hasUnsavedChanges(): boolean {
    return this.campusWardForm.dirty;
  }

  /**
   * Gets the message to give the user when we have unsaved changes and they're trying to navigate away.
   */
  get unsavedMessage(): string {
    return 'You have unsaved changes to the ward\'s information.  Are you sure you want to continue?';
  }

  /**
   * Indicates that the ward's unit number is unassigned; really just assigns it to our placeholder value.
   */
  setUnassignedUnitNumber() {
    this.campusWardForm
      .get('unitNumber')
      .setValue(Config.Constants.unassignedUnitNumber);
  }

  /**
   * Indicates that the ward's unit number should be entered; really just clears the placeholder value that was there.
   */
  clearUnassignedUnitNumber() {
    if (
      this.campusWard &&
      this.campusWard.unitNumber !== Config.Constants.unassignedUnitNumber
    ) {
      this.campusWardForm
        .get('unitNumber')
        .setValue(this.campusWard.unitNumber);
    } else {
      this.campusWardForm.get('unitNumber').setValue('');
    }
  }

  /**
   * Indicates whether or not our form group wants the ward to have a unit number.
   */
  unitNumberAssigned() {
    return (
      this.campusWardForm.get('unitNumber').value !==
      Config.Constants.unassignedUnitNumber
    );
  }

  /**
   * Tells you if the given form control has the given validation error.
   */
  hasError(formControl: FormControl, error: string): boolean {
    return (
      (formControl.touched || formControl.dirty) && formControl.hasError(error)
    );
  }

  /**
   * Whether or not we're editing or adding.
   */
  isEditing() {
    return this.campusWard.campusWardId > 0;
  }

  /**
   * Load the selected ward's details so the user can modify them.
   */
  loadSelectedWardDetails() {
    if (!this.loadingWardDetails && this.selectedCampusWard) {
      this.resetSavedDeletedFlags();

      this.loadingWardDetails = true;
      this.wardService
        .getCampusWardDetailsForUpdate(this.selectedCampusWard.campusWardId)
        .pipe(
          finalize(() => (this.loadingWardDetails = false))
        )
        .subscribe(ward => {
          this.setWard(ward);
        });
    }
  }

  /**
   * Gets our component ready for the user to add a new campus ward.
   */
  createNewCampusWard() {
    this.clearSelectedWard();
    this.resetSavedDeletedFlags();

    const ward = new CampusWardDetailsForUpdate();
    ward.campusWardId = 0;
    this.setWard(ward);
  }

  /**
   * Save's the ward information, either creating a new one or updating it.
   */
  save() {
    if (
      this.saving ||
      !this.campusWardForm.dirty ||
      !this.campusWardForm.valid
    ) {
      return;
    }

    // get our data from the form
    const ward = Object.assign({}, this.campusWard, this.campusWardForm.value);

    this.resetSavedDeletedFlags();

    this.saving = true;
    if (this.isEditing()) {
      this.wardService
        .updateCampusWard(ward)
        .pipe(
          finalize(() => (this.saving = false))
        )
        .subscribe(
          updatedWard => {
            this.setWard(updatedWard);

            // replace it in our dropdown in case they changed the name
            const index = this.campusWards.findIndex(
              w => w.campusWardId === updatedWard.campusWardId
            );
            this.campusWards.splice(index, 1);
            this.campusWards.push(updatedWard);

            // mark it as select it
            this.selectedCampusWard = updatedWard;

            this.saved = true;
          },
          error => {
            this.showError(error);
          }
        );
    } else {
      this.wardService
        .createCampusWard(ward)
        .pipe(
          finalize(() => (this.saving = false))
        )
        .subscribe(
          newWard => {
            // set it, add it to our dropdown, select it in the dropdown so we're in edit mode
            this.setWard(newWard);
            this.campusWards.push(newWard);
            this.selectedCampusWard = newWard;

            this.saved = true;
          },
          error => {
            this.showError(error);
          }
        );
    }
  }

  /**
   * Gets rid of the ward the user was editing.
   */
  cancel() {
    this.clearSelectedWard();
  }

  /**
   * Deletes the selected ward.
   */
  delete() {
    if (!this.isEditing() || this.deleting) {
      return;
    }

    const toDelete = this.campusWard;

    const message = `Are you sure you want to delete ${
      toDelete.name
    } and its associated leadership and other ward information? This action cannot be undone.`;
    const confirmDialog = this.dialog.open(ConfirmDialog, {
      autoFocus: false,
      data: { title: 'Please confirm', message: message }
    });

    confirmDialog.afterClosed().subscribe(response => {
      if (response) {
        this.resetSavedDeletedFlags();

        this.deleting = true;
        this.wardService
          .deleteCampusWard(toDelete.campusWardId)
          .pipe(
            finalize(() => (this.deleting = false))
          )
          .subscribe(
            () => {
              // remove the ward from our list
              const index = this.campusWards.findIndex(
                w => w.campusWardId === toDelete.campusWardId
              );
              this.campusWards.splice(index, 1);

              this.deleted = true;
              this.clearSelectedWard();
            },
            error => {
              this.showError(error);
            }
          );
      }
    });
  }

  /**
   * Shows an error dialog to the user.
   */
  private showError(message: string) {
    this.dialog.open(MessageDialog, {
      data: { message: message, isError: true }
    });
  }

  /**
   * Clears the selected ward from the dropdown as well as the form.
   */
  private clearSelectedWard() {
    this.campusWard = null;
    this.selectedCampusWard = null;
  }

  /**
   * Resets the flags that we use to show a save or delete completed successfully.
   */
  private resetSavedDeletedFlags() {
    this.saved = false;
    this.deleted = false;
  }

  /**
   * Handles the things that need done when we've loaded a ward's information or after an update/create.
   */
  private setWard(wardDetails: CampusWardDetailsForUpdate) {
    this.campusWard = wardDetails;

    if (this.campusWard.campusWardId === 0) {
      this.actionTitle = 'Add a New Ward';
    } else {
      this.actionTitle = `Update Information: ${this.campusWard.name}`;
    }

    this.campusWardForm.reset();
    this.campusWardForm.patchValue(this.campusWard);
  }

  /**
   * Loads the dropdown with our wards.
   */
  private loadData() {
    if (!this.loading) {
      this.loading = true;
      this.wardService
        .getCampusWards()
        .pipe(
          finalize(() => (this.loading = false))
        )
        .subscribe(
          data => {
            this.campusWards = data;
          },
          error => {
            this.hasLoadingError = true;
          }
        );
    }
  }

  /**
   * Show or hide password
   */
  togglePassoWordVisibilty() {
    if (this.showPassword) {
      this.showPassword = false;
    } else {
      this.showPassword = true;
    }
  }
}
